/**
 * Журнал финансовых операций — таблица + фильтры + CRUD через модалку.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { PeriodPicker } from "@/components/PeriodPicker";
import { ExpenseFormModal } from "@/components/finance/ExpenseFormModal";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import {
  fetchExpenses, fetchExpenseCategories, deleteExpense,
  formatRub, KIND_LABELS, KIND_COLORS, PAYMENT_LABELS,
  type ExpenseRow, type PaymentMethod, type CategoryKind,
} from "@/api/finance";
import { ApiError } from "@/api/client";
import { usePeriod } from "@/state/period";


export function FinanceExpensesPage() {
  const { from, to } = usePeriod();
  const qc = useQueryClient();
  const [categoryFilter, setCategoryFilter] = useState<number | "">("");
  const [paymentFilter, setPaymentFilter] = useState<PaymentMethod | "">("");
  const [attachmentFilter, setAttachmentFilter] = useState<"" | "with" | "without">("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseRow | null>(null);

  const expensesQ = useQuery({
    queryKey: ["finance-expenses", from, to, categoryFilter, paymentFilter],
    queryFn: () => fetchExpenses({
      date_from: from,
      date_to: to,
      category_id: categoryFilter || undefined,
      payment_method: paymentFilter || undefined,
    }),
  });
  const categoriesQ = useQuery({
    queryKey: ["finance-categories", false],
    queryFn: () => fetchExpenseCategories(false),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: number) => {
      try { await deleteExpense(id); }
      catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          if (confirm(`${e.message}\n\nВсё равно удалить?`)) {
            await deleteExpense(id, { force: true });
          } else { throw new Error("Удаление отменено"); }
        } else { throw e; }
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance-expenses"] });
      void qc.invalidateQueries({ queryKey: ["finance-overview"] });
    },
    onError: (e: Error) => { if (e.message !== "Удаление отменено") alert("Ошибка: " + e.message); },
  });

  if (expensesQ.isLoading || categoriesQ.isLoading) {
    return <Spinner size="lg" label="Загрузка журнала…" />;
  }
  if (expensesQ.error) {
    const err = expensesQ.error;
    if (err instanceof ApiError && err.status === 403) {
      return (
        <div className="alert alert-amber">
          <div className="alert-icon"><Icon name="warn" size={16}/></div>
          <div className="alert-body">
            <div className="alert-title">Доступ ограничен</div>
            <div>Финансовый раздел доступен только администратору и руководителю отдела.</div>
          </div>
        </div>
      );
    }
    const msg = err instanceof ApiError ? `HTTP ${err.status}` : (err as Error).message;
    return <div className="alert alert-red"><div className="alert-body">{msg}</div></div>;
  }

  const rawData = expensesQ.data!;
  const categories = categoriesQ.data || [];

  // Применяем фильтр по наличию чека на клиенте (бэкенд не поддерживает отдельно)
  const filteredItems = rawData.items.filter(it => {
    if (attachmentFilter === "with" && !it.attachment_path) return false;
    if (attachmentFilter === "without" && it.attachment_path) return false;
    return true;
  });
  const data = {
    ...rawData,
    items: filteredItems,
    count: filteredItems.length,
    total_amount: filteredItems.reduce((a, b) => a + b.amount, 0),
  };

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }
  function openEdit(row: ExpenseRow) {
    setEditing(row);
    setModalOpen(true);
  }
  function confirmDelete(row: ExpenseRow) {
    const ok = window.confirm(
      `Удалить операцию от ${row.date} на ${formatRub(row.amount)}?\n\n` +
      `${row.category_name || "—"} · ${row.supplier || row.description || ""}`,
    );
    if (ok) deleteMut.mutate(row.id);
  }

  return (
    <FinanceLayout>
            {/* Header strip */}
      <div className="filter-bar" style={{ justifyContent: "space-between", marginBottom: "var(--gap)" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select
            className="input-sm" value={categoryFilter === "" ? "" : String(categoryFilter)}
            onChange={e => setCategoryFilter(e.target.value ? parseInt(e.target.value, 10) : "")}
            style={{ minWidth: 200 }}
          >
            <option value="">Все категории</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            className="input-sm" value={paymentFilter}
            onChange={e => setPaymentFilter(e.target.value as PaymentMethod | "")}
            style={{ minWidth: 140 }}
          >
            <option value="">Любая оплата</option>
            {(Object.keys(PAYMENT_LABELS) as PaymentMethod[]).map(m => (
              <option key={m} value={m}>{PAYMENT_LABELS[m]}</option>
            ))}
          </select>
          {/* Фильтр по наличию чека/документа */}
          <div className="chip-row" style={{ marginBottom: 0 }}>
            <button type="button"
                    className={`chip ${attachmentFilter === "" ? "active" : ""}`}
                    onClick={() => setAttachmentFilter("")}>Все</button>
            <button type="button"
                    className={`chip ${attachmentFilter === "with" ? "active" : ""}`}
                    onClick={() => setAttachmentFilter("with")}
                    title="Только с прикреплённым чеком/документом"><span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="paperclip" size={11}/>С чеком</span></button>
            <button type="button"
                    className={`chip ${attachmentFilter === "without" ? "active" : ""}`}
                    onClick={() => setAttachmentFilter("without")}
                    title="Только без подтверждающего документа"
                    style={attachmentFilter === "without"
                      ? { background: "var(--brick-50)", color: "var(--brick-deep)", borderColor: "var(--brick-100)" }
                      : undefined}><span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="warn" size={11}/>Без чека</span></button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <PeriodPicker />
          <button className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={12}/> Новая операция
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="row row-3" style={{ marginBottom: "var(--gap)" }}>
        <div className="kpi">
          <div className="kpi-label">Всего операций</div>
          <div className="kpi-value kpi-value-sm">{data.count}</div>
          <div className="kpi-foot">
            <span style={{ fontSize: 12, color: "var(--mute)" }}>{data.period.from} – {data.period.to}</span>
          </div>
        </div>
        <div className="kpi alarm">
          <div className="kpi-label">Сумма расходов</div>
          <div className="kpi-value kpi-value-sm">
            {formatRub(data.total_amount, { unit: false })}
            <span className="kpi-unit">{Math.abs(data.total_amount) >= 1_000_000 ? " млн ₽" : " тыс ₽"}</span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Средний чек</div>
          <div className="kpi-value kpi-value-sm">
            {formatRub(data.count > 0 ? data.total_amount / data.count : 0, { unit: false })}
            <span className="kpi-unit"> ₽</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {data.items.length === 0 ? (
          <div style={{ padding: "32px 20px", textAlign: "center" }}>
            <div style={{ fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700, marginBottom: 6, color: "var(--ink)" }}>
              Операций не найдено
            </div>
            <div style={{ fontSize: 13, color: "var(--mute)", marginBottom: 12 }}>
              Поменяйте фильтры или добавьте первую операцию
            </div>
            <button className="btn btn-primary" onClick={openCreate}>
              <Icon name="plus" size={12}/> Новая операция
            </button>
          </div>
        ) : (
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Дата</th>
                  <th>Категория</th>
                  <th>Поставщик / Описание</th>
                  <th style={{ width: 110 }}>Оплата</th>
                  <th className="num" style={{ width: 130 }}>Сумма</th>
                  <th style={{ width: 80 }}/>
                </tr>
              </thead>
              <tbody>
                {data.items.map(e => (
                  <tr key={e.id}>
                    <td className="tabular">{e.date}</td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{
                          width: 8, height: 8, borderRadius: 2,
                          background: KIND_COLORS[(e.category_kind || "other") as CategoryKind],
                        }}/>
                        <span className="strong">{e.category_name || "—"}</span>
                        <span style={{ fontSize: 10.5, color: "var(--mute)" }}>
                          · {KIND_LABELS[(e.category_kind || "other") as CategoryKind]}
                        </span>
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {e.supplier ? <strong>{e.supplier}</strong> : null}
                          {e.supplier && e.description ? <span style={{ color: "var(--mute)" }}> · </span> : null}
                          {e.description ? <span>{e.description}</span> : null}
                          {!e.supplier && !e.description ? <span className="muted">—</span> : null}
                          {e.created_by_name && (
                            <div style={{ fontSize: 10.5, color: "var(--mute)", marginTop: 2 }}>
                              внёс: {e.created_by_name}
                            </div>
                          )}
                        </div>
                        {/* Индикатор наличия чека */}
                        {e.attachment_path ? (
                          <span title="Чек/документ прикреплён"
                                style={{ color: "var(--green)", flexShrink: 0, display: "inline-flex" }}><Icon name="paperclip" size={13}/></span>
                        ) : (
                          <span title="Без подтверждающего документа"
                                style={{
                                  fontSize: 10, padding: "1px 6px", borderRadius: 999,
                                  background: "var(--brick-50)", color: "var(--brick-deep)",
                                  border: "1px solid var(--brick-100)",
                                  whiteSpace: "nowrap", flexShrink: 0,
                                  fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4,
                                  display: "inline-flex", alignItems: "center", gap: 3,
                                }}><Icon name="warn" size={9}/>нет чека</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="badge">{PAYMENT_LABELS[e.payment_method]}</span>
                    </td>
                    <td className="num strong tabular">{formatRub(e.amount)}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button
                          className="btn btn-sm" title="Редактировать"
                          onClick={() => openEdit(e)}
                        >
                          <Icon name="edit" size={12}/>
                        </button>
                        <button
                          className="btn btn-sm" title="Удалить"
                          onClick={() => confirmDelete(e)}
                          disabled={deleteMut.isPending}
                          style={{ color: "var(--brick)" }}
                        >
                          <Icon name="trash" size={12}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ExpenseFormModal
        open={modalOpen}
        initial={editing}
        categories={categories}
        onClose={() => setModalOpen(false)}
        onSaved={() => { /* invalidation в мутации */ }}
      />
    </FinanceLayout>
  );
}
